import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { 
  PromptsSection,
  PromptsSectionTitle,
  PromptsGrid,
  PromptCardNew,
  PromptCardContent,
  PromptIconNew,
  PromptTextContent,
  PromptTitleTypographyNew,
  PromptTooltipTypographyNew,
  FavoriteButton
} from '../../styles/StyledComponents';

const PromptsDrawer = ({
  open,
  onClose,
  customPrompts,
  onAddPrompt,
  onEditPrompt,
  onDeletePrompt,
  favoritePrompts,
  onFavoritePrompt,
  additionalPrompts,
  onPromptClick,
}) => {
  const drawerWidth = {
    xs: '280px',
    sm: '300px',
    md: '320px',
    lg: '340px'
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: open ? 0 : `-${drawerWidth.lg}`,
        top: 0,
        bottom: 0,
        width: { xs: drawerWidth.xs, sm: drawerWidth.sm, md: drawerWidth.md, lg: drawerWidth.lg },
        backgroundColor: 'background.default',
        borderLeft: '1px solid',
        borderColor: 'divider',
        transition: 'right 0.3s ease-in-out',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PromptsSection>
        <Box sx={{ 
          mb: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1
        }}>
          <PromptsSectionTitle>
            Prompts
          </PromptsSectionTitle>
          <IconButton onClick={onAddPrompt} size="small">
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Favorite Prompts Section */}
        {favoritePrompts.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
              Favorites
            </Typography>
            <PromptsGrid>
              {favoritePrompts.map((prompt) => (
                <PromptCardNew key={prompt.id} onClick={() => onPromptClick(prompt)}>
                  <PromptCardContent>
                    <PromptIconNew>{prompt.icon}</PromptIconNew>
                    <PromptTextContent>
                      <PromptTitleTypographyNew>{prompt.title}</PromptTitleTypographyNew>
                      <PromptTooltipTypographyNew>{prompt.tooltip}</PromptTooltipTypographyNew>
                    </PromptTextContent>
                    <Tooltip title={favoritePrompts.includes(prompt.id) ? "Remove from favorites" : "Add to favorites"}>
                      <FavoriteButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onFavoritePrompt(prompt.id);
                        }}
                        isFavorite={favoritePrompts.includes(prompt.id)}
                      >
                        {favoritePrompts.includes(prompt.id) ? (
                          <StarIcon />
                        ) : (
                          <StarBorderIcon />
                        )}
                      </FavoriteButton>
                    </Tooltip>
                  </PromptCardContent>
                </PromptCardNew>
              ))}
            </PromptsGrid>
          </Box>
        )}

        {/* Custom Prompts Section */}
        {customPrompts.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
              Custom Prompts
            </Typography>
            <PromptsGrid>
              {customPrompts.map((prompt) => (
                <PromptCardNew key={prompt.id} onClick={() => onPromptClick(prompt)}>
                  <PromptCardContent>
                    <PromptIconNew>{prompt.icon}</PromptIconNew>
                    <PromptTextContent>
                      <PromptTitleTypographyNew>{prompt.title}</PromptTitleTypographyNew>
                      <PromptTooltipTypographyNew>{prompt.tooltip}</PromptTooltipTypographyNew>
                    </PromptTextContent>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditPrompt(prompt);
                          }}
                          sx={{ 
                            p: 0.5,
                            color: 'grey.500',
                            '&:hover': { color: 'primary.main' },
                            '& .MuiSvgIcon-root': { 
                              fontSize: { xs: 14, sm: 16 }
                            }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeletePrompt(prompt.id);
                          }}
                          sx={{ 
                            p: 0.5,
                            color: 'grey.500',
                            '&:hover': { color: 'error.main' },
                            '& .MuiSvgIcon-root': { 
                              fontSize: { xs: 14, sm: 16 }
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </PromptCardContent>
                </PromptCardNew>
              ))}
            </PromptsGrid>
          </Box>
        )}

        {/* Predefined Prompts Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
            Predefined Prompts
          </Typography>
          <PromptsGrid>
            {additionalPrompts.map((prompt) => (
              <PromptCardNew key={prompt.id} onClick={() => onPromptClick(prompt)}>
                <PromptCardContent>
                  <PromptIconNew>{prompt.icon}</PromptIconNew>
                  <PromptTextContent>
                    <PromptTitleTypographyNew>{prompt.title}</PromptTitleTypographyNew>
                    <PromptTooltipTypographyNew>{prompt.tooltip}</PromptTooltipTypographyNew>
                  </PromptTextContent>
                  <Tooltip title={favoritePrompts.includes(prompt.id) ? "Remove from favorites" : "Add to favorites"}>
                    <FavoriteButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFavoritePrompt(prompt.id);
                      }}
                      isFavorite={favoritePrompts.includes(prompt.id)}
                    >
                      {favoritePrompts.includes(prompt.id) ? (
                        <StarIcon />
                      ) : (
                        <StarBorderIcon />
                      )}
                    </FavoriteButton>
                  </Tooltip>
                </PromptCardContent>
              </PromptCardNew>
            ))}
          </PromptsGrid>
        </Box>
      </PromptsSection>
    </Box>
  );
};

export default PromptsDrawer;
